import { fromPairs, mapValues } from "lodash";

import { gql, IdentityContextProvider } from "@examspark/api";
import { exists } from "@examspark/common";
import {
  ControllerContextInput,
  ExportDocumentResponse,
} from "@examspark/types";

export type PrintHandlerResponse =
  | { success: false; error: string }
  | { success: true; url: string };

export const printHandler = async (
  input: ControllerContextInput<any>,
  options?: { devPdfServerUrl?: string },
): Promise<PrintHandlerResponse> => {
  if (options?.devPdfServerUrl) {
    return localExpressPrintHandler(input, options.devPdfServerUrl);
  } else {
    return graphQLPrintHandler(input);
  }
};

export const localExpressPrintHandler = async (
  input: ControllerContextInput<any>,
  serverUrl: string,
): Promise<PrintHandlerResponse> => {
  const { action, params } = input;

  // This to get around `useSystemAdmin: undefined` mapping to `?useSystemAdmin=`
  // without this filter check, which results in a failed Zod check when it ends
  // up as empty string.
  const paramsWithIdentityContext = fromPairs(
    Object.entries(
      Object.assign(IdentityContextProvider.getContext(), params),
    ).filter(([_, v]) => exists(v)),
  );

  const queryParams = new URLSearchParams(
    mapValues(paramsWithIdentityContext, (p) => p?.toString() ?? ""),
  );

  return {
    success: true,
    url: `${serverUrl}/${action}?${queryParams}`,
  };
};

export const graphQLPrintHandler = async (
  input: ControllerContextInput<any>,
): Promise<PrintHandlerResponse> => {
  const { action, params } = input;

  let operation: Promise<ExportDocumentResponse | undefined>;

  switch (action) {
    case "PrintExam":
    case "PrintExamAnswerKey":
      operation = gql.operations.jobs
        .printExam({ ...params })
        .then((r) => r.data?.printExam);
      break;
    case "PrintTcmpcVerificationDocument":
      operation = gql.operations.jobs
        .printVerificationDocument({ ...params })
        .then((r) => r.data?.printVerificationDocument);
      break;
    case "PrintYearAtaGlance":
      operation = gql.operations.jobs
        .printYearAtAGlanceDocument({ ...params })
        .then((r) => r.data?.printYearAtaGlance);
      break;
    case "PrintPerformanceAssessment": {
      const { workspaceId, ...rest } = params;
      operation = gql.operations.jobs
        .printPerformanceAssessmentDocument({ ...rest })
        .then((r) => r.data?.printPerformanceAssessment);
      break;
    }
    default:
      return {
        success: false,
        error: `Unrecognized document action ${action}.`,
      };
  }

  const response = await operation.catch(() => undefined);

  switch (response?.__typename) {
    case "ExportDocumentSuccessResponse":
      return {
        success: true,
        url: response.resultUrl,
      };
    default:
      return {
        success: false,
        error: "PDF failed to generate.",
      };
  }
};
